import { useMemo } from 'react';
import styled from 'styled-components';

import { AnalyticalExaminationType } from '@dreryk/drerykweb-medical-dictionaries-api';

import {
   EmptyStatePlaceholder,
   FormLabel,
   LoaderSpinner,
   resolveInputLayoutStyles,
   Table,
} from '@dreryk/react-components';

import { analyticalExaminationTranslationKeys } from '../../../translations';

import { AutosuggestSelectorReturnType } from './AnalyticalExaminationTypeAutosuggest.hooks';
import {
   useFrequentAnalyticalExaminationsTableConfiguration,
   useMostFrequentlyUsedAnalyticalExaminationTypesQuery,
} from './FrequentAnalyticalExaminationsTable.hooks';

type FrequentAnalyticalExaminationsTableProps = {
   readonly selectFrequentAnalyticalExamination: AutosuggestSelectorReturnType<AnalyticalExaminationType>['addItem'];
   readonly selectedItems: AnalyticalExaminationType[];
};

const StyledFrequentAnalyticalExaminationsTable = styled.div`
   ${resolveInputLayoutStyles};
`;

StyledFrequentAnalyticalExaminationsTable.displayName = 'StyledFrequentAnalyticalExaminationsTable';

const FrequentAnalyticalExaminationsTable = (props: FrequentAnalyticalExaminationsTableProps): JSX.Element | null => {
   const { loading, data: frequentExaminationTypes } = useMostFrequentlyUsedAnalyticalExaminationTypesQuery();

   const dataConfiguration = useMemo(() => {
      return {
         data:
            frequentExaminationTypes?.filter((frequentExaminationType) => {
               return !props.selectedItems.find(
                  (selectedExaminationType) => selectedExaminationType.id === frequentExaminationType.id
               );
            }) ?? [],
      };
   }, [frequentExaminationTypes, props.selectedItems]);

   const tableConfiguration = useFrequentAnalyticalExaminationsTableConfiguration(
      props.selectFrequentAnalyticalExamination
   );

   return (
      <StyledFrequentAnalyticalExaminationsTable>
         <FormLabel
            label={analyticalExaminationTranslationKeys.mostCommonExaminations}
            testId={'frequentExaminationsLabel'}
         />
         {loading ? <LoaderSpinner /> : null}
         {!loading && (
            <Table
               compact
               testId={'frequentExaminationsTable'}
               dataConfiguration={dataConfiguration}
               placeholder={
                  <EmptyStatePlaceholder
                     heading={analyticalExaminationTranslationKeys.noFrequentAnalyticalExaminations}
                     subheading={analyticalExaminationTranslationKeys.noFrequentAnalyticalExaminationSubText}
                     testId={'noFrequentExaminations'}
                  />
               }
               columns={tableConfiguration}
               keyExtractor={'id'}
            />
         )}
      </StyledFrequentAnalyticalExaminationsTable>
   );
};

export { FrequentAnalyticalExaminationsTable };
